import { formatAppointmentDateOnlyInClinic } from './clinicTime';

export const EVOLUTION_NOTE_PREVIEW_STORAGE_KEY = 'centrocultural_evolution_note_preview_v1';

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Atributo src/href: no romper URLs con & ni data: */
function escapeAttr(s) {
  return String(s || '').replace(/"/g, '&quot;');
}

function formatNoteDate(ymd) {
  const raw = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '—';
  try {
    return formatAppointmentDateOnlyInClinic(raw, "d 'de' MMMM 'de' yyyy");
  } catch {
    return raw;
  }
}

function block(label, value) {
  const text = String(value || '').trim();
  const body = text
    ? `<div class="block-body">${escapeHtml(text).replace(/\n/g, '<br />')}</div>`
    : '<div class="block-body empty">—</div>';
  return `
    <section class="note-block">
      <h3 class="block-label">${escapeHtml(label)}</h3>
      ${body}
    </section>`;
}

const PRINT_TEXT_API_KEYS = ['chief_complaint', 'procedure', 'diagnosis', 'indications'];

export function evolutionNotePrintHasContent(apiPrint) {
  if (!apiPrint) return false;
  return PRINT_TEXT_API_KEYS.some((k) => String(apiPrint[k] || '').trim() !== '');
}

/** Fusiona borrador del formulario solo si el campo tiene texto (post-GET). */
export function mergeFormDraftIntoApiPrint(apiPrint, formDraft) {
  if (!apiPrint || !formDraft) return apiPrint;
  const out = { ...apiPrint };
  const draftToApi = [
    ['encounter_date', 'encounter_date'],
    ['chief_complaint', 'chief_complaint'],
    ['clinical_notes', 'procedure'],
    ['diagnosis', 'diagnosis'],
    ['treatment_plan', 'indications'],
  ];
  draftToApi.forEach(([draftKey, apiKey]) => {
    const v = String(formDraft[draftKey] ?? '').trim();
    if (v) out[apiKey] = v;
  });
  return out;
}

/**
 * Convierte respuesta GET .../evolution-note-print al payload HTML.
 */
export function htmlPayloadFromApiPrint(apiPrint, logoDataUrl = '') {
  const logoUrl = apiPrint?.logo_url || '';
  return {
    logoUrl,
    logoDataUrl: logoDataUrl || logoUrl,
    clinicName: apiPrint?.clinic_name || 'Centro Cultural',
    clinicAddress: apiPrint?.clinic_address || '',
    clinicPhone: apiPrint?.clinic_phone || '',
    folio: apiPrint?.folio || '',
    encounterDate: apiPrint?.encounter_date || '',
    patientName: apiPrint?.patient_name || '',
    chiefComplaint: apiPrint?.chief_complaint || '',
    procedure: apiPrint?.procedure || '',
    diagnosis: apiPrint?.diagnosis || '',
    indications: apiPrint?.indications || '',
    podiatristName: apiPrint?.podiatrist_name || '',
    podiatristLicense: apiPrint?.podiatrist_license || '',
  };
}

export function buildEvolutionNoteContentHtml({
  logoUrl = '',
  logoDataUrl = '',
  clinicName = 'Centro Cultural',
  clinicAddress = '',
  clinicPhone = '',
  folio = '',
  encounterDate = '',
  patientName = '',
  chiefComplaint = '',
  procedure = '',
  diagnosis = '',
  indications = '',
  podiatristName = '',
  podiatristLicense = '',
}) {
  const dateLabel = formatNoteDate(encounterDate);
  const logoSrc = logoDataUrl || logoUrl;
  const logoBlock = logoSrc
    ? `<img class="logo" src="${escapeAttr(logoSrc)}" alt="${escapeHtml(clinicName)}" />`
    : '';

  const metaRows = [
    folio ? `<span><strong>Folio:</strong> ${escapeHtml(folio)}</span>` : '',
    `<span><strong>Fecha:</strong> ${escapeHtml(dateLabel)}</span>`,
  ]
    .filter(Boolean)
    .join(' · ');

  const doctorLine = [
    podiatristName,
    podiatristLicense ? `Cédula profesional: ${podiatristLicense}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return `
    <header class="doc-header">
      ${logoBlock}
      <div class="clinic-meta">
        <p class="clinic-name">${escapeHtml(clinicName)}</p>
        ${clinicAddress ? `<p class="clinic-detail">${escapeHtml(clinicAddress)}</p>` : ''}
        ${clinicPhone ? `<p class="clinic-detail">${escapeHtml(clinicPhone)}</p>` : ''}
      </div>
    </header>
    <h1 class="doc-title">NOTA DE SEGUIMIENTO</h1>
    <p class="meta-line">${metaRows}</p>
    <p class="patient-line"><strong>Paciente:</strong> ${escapeHtml(patientName || '—')}</p>
    ${block('Motivo de consulta', chiefComplaint)}
    ${block('Tratamiento / Procedimiento realizado', procedure)}
    ${block('Diagnóstico', diagnosis)}
    ${block('Indicaciones', indications)}
    <footer class="doc-footer">
      ${doctorLine ? `<p class="doctor-line"><strong>Médico(a):</strong> ${escapeHtml(doctorLine)}</p>` : ''}
      <p class="footer-hint">Documento generado desde el expediente clínico.</p>
    </footer>
  `;
}

/** Estilos para la vista previa React en /admin/nota-evolucion-preview */
export const EVOLUTION_NOTE_PRINT_PAGE_STYLES = `
  body.evolution-note-preview-open {
    margin: 0;
    background: #fff;
  }
  body.evolution-note-preview-open #root {
    min-height: 100vh;
  }
  .evolution-note-print-page {
    box-sizing: border-box;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 12pt;
    line-height: 1.5;
    color: #111;
    max-width: 210mm;
    margin: 0 auto;
    padding: 18mm 16mm;
    background: #fff;
  }
  .evolution-note-print-page * { box-sizing: border-box; }
  .evolution-note-print-page .doc-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 8mm;
    padding-bottom: 4mm;
    border-bottom: 1px solid #ccc;
  }
  .evolution-note-print-page .logo {
    max-height: 22mm;
    max-width: 55mm;
    object-fit: contain;
  }
  .evolution-note-print-page .clinic-meta { flex: 1; min-width: 140px; font-family: system-ui, sans-serif; }
  .evolution-note-print-page .clinic-name { font-weight: 700; font-size: 13pt; margin: 0 0 2px; }
  .evolution-note-print-page .clinic-detail { font-size: 10pt; color: #444; margin: 0; }
  .evolution-note-print-page .doc-title {
    font-family: system-ui, sans-serif;
    font-size: 14pt;
    text-align: center;
    margin: 0 0 5mm;
    letter-spacing: 0.04em;
  }
  .evolution-note-print-page .meta-line,
  .evolution-note-print-page .patient-line {
    font-family: system-ui, sans-serif;
    font-size: 11pt;
    margin: 0 0 3mm;
  }
  .evolution-note-print-page .note-block { margin: 0 0 5mm; }
  .evolution-note-print-page .block-label {
    font-family: system-ui, sans-serif;
    font-size: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin: 0 0 2mm;
    color: #333;
  }
  .evolution-note-print-page .block-body {
    border: 1px solid #bbb;
    border-radius: 4px;
    min-height: 18mm;
    padding: 3mm 4mm;
    white-space: pre-wrap;
  }
  .evolution-note-print-page .block-body.empty { color: #888; }
  .evolution-note-print-page .doc-footer {
    margin-top: 10mm;
    padding-top: 4mm;
    border-top: 1px solid #ccc;
    font-family: system-ui, sans-serif;
    font-size: 10pt;
  }
  .evolution-note-print-page .doctor-line { font-weight: 600; margin: 0 0 2mm; }
  .evolution-note-print-page .footer-hint { color: #666; margin: 0; font-size: 9pt; }
  .evolution-note-print-page .evolution-note-print-toolbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #1a4d3a;
    color: #fff;
    padding: 10px 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    z-index: 100;
    font-family: system-ui, sans-serif;
    font-size: 14px;
  }
  .evolution-note-print-page .evolution-note-print-toolbar button {
    background: #fff;
    color: #1a4d3a;
    border: none;
    padding: 8px 16px;
    border-radius: 999px;
    font-weight: 600;
    cursor: pointer;
  }
  .evolution-note-print-page .evolution-note-print-toolbar button:hover { background: #e8f5ee; }
  .evolution-note-print-page .toolbar-hint { opacity: 0.9; font-size: 12px; flex: 1; min-width: 200px; }
  .evolution-note-print-page.has-toolbar { padding-top: calc(18mm + 56px); }
  @media print {
    body.evolution-note-preview-open { background: #fff; }
    .evolution-note-print-page .evolution-note-print-toolbar { display: none !important; }
    .evolution-note-print-page.has-toolbar { padding-top: 18mm; }
  }
`;

export function evolutionNotePreviewUrl({ autoPrint = false } = {}) {
  const qs = autoPrint ? '?print=1' : '';
  return `${window.location.origin}/admin/nota-evolucion-preview${qs}`;
}

export function buildEvolutionNoteHtml(payload) {
  const body = buildEvolutionNoteContentHtml(payload);
  const patientName = payload?.patientName || 'Paciente';

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Nota de evolución — ${escapeHtml(patientName)}</title>
</head>
<body>
  <div class="doc-content">${body}</div>
</body>
</html>`;
}

export function openEvolutionNotePreview(payload, { autoPrint = false } = {}) {
  const serialized = JSON.stringify(payload);
  try {
    localStorage.setItem(EVOLUTION_NOTE_PREVIEW_STORAGE_KEY, serialized);
    sessionStorage.setItem(EVOLUTION_NOTE_PREVIEW_STORAGE_KEY, serialized);
  } catch {
    /* ignore */
  }
  const win = window.open(evolutionNotePreviewUrl({ autoPrint }), '_blank', 'noopener,noreferrer');
  if (!win) {
    return { ok: false, reason: 'popup_blocked' };
  }
  return { ok: true };
}

export function readEvolutionNotePreviewPayload() {
  try {
    const raw =
      localStorage.getItem(EVOLUTION_NOTE_PREVIEW_STORAGE_KEY) ||
      sessionStorage.getItem(EVOLUTION_NOTE_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
